import { CircularProgress } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import ProgressIndicator from "./ProgressIndicator";
import SingleAppointmentComponent from "./SingleAppointmentComponent";
import backImage from "../../../assets/icons/arrow-back-black.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  createAppointments,
  rescheduleAppointments,
  rescheduleLabtestAppointments,
  navigoShowError,
  setStateFromPayload,
  updateCurrentScreen,
  getAllTimeSlots,
  getReasons,
} from "../navigoSlice";
import { logEvent, logEventWithData } from "../../../app/eventSlice";
import noSlotVector from "../../../assets/images/no-slots.svg";
import tickimg from "../../../assets/images/tick.png";
import moment from "moment";
import {
  createDateAndChangeScreen,
  errorMessageHandler,
  stringToIntTime,
  uploadNavigoDocuments,
  mapDocumentsToAppointment,
} from "../utilityFunctions";
import { useHistory } from "react-router";
import navigoServices from "../../../services/NavigoServices";
import { IonContent, IonPage, NavContext } from "@ionic/react";
import BackButton from "../../../components/common/BackButton";
import { useLocation } from "react-router";
import { withFrom } from "../utils/navBack";

/**
 * Slots / confirm screen.
 *
 * Exported in two shapes on purpose. Ionic's contract is ONE IonPage per route:
 * every IonPage calls registerIonPage(el, routeInfo), which overwrites
 * viewItem.ionPageElement for the CURRENT route. A page nested inside a routed
 * page therefore steals that route's element, and Ionic later commits a DOM
 * node that is not the page — the URL changes and the screen does not.
 *
 *   NavigoFinalSectionContent - no IonPage. Use this everywhere this screen is
 *                               rendered INSIDE another page.
 *   NavigoFinalSection        - wraps Content in IonPage/IonContent. Only for a
 *                               route, where it is the page.
 */
export function NavigoFinalSectionContent({
  addressId: addressId,
  familyMember: familyMember,
  provider: provider,
  memberRelation: memberRelation,
  service: services,
  price: price,
  pageType,
  isCmp,
  onBack,
}) {
  const { state } = useLocation();
  const location = useLocation();
  const { body } = location.state || {};
  // Read the service type off the committed service, not the URL. This screen
  // is rendered inline inside later screens (/providerCard, /bookingFor, ...)
  // whose history.push carries no query string, so the URL-derived value fell
  // back to 1 (consultation) and the reason screen was shown for health
  // checkups too. The query param stays as a fallback for the legacy
  // /navigo?serviceType= render, which mounts this without a service prop.
  const serviceType =
    Number(services?.serviceTypeId) ||
    Number(new URLSearchParams(location.search).get("serviceType")) ||
    1;
  const navigoStore = useSelector((store) => store.navigo);
  const patientProfile = useSelector(
    (store) => store.login.user.patientProfile,
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const navContext = useContext(NavContext);
  const [isLoading, setIsLoading] = useState(false);
  const { config } = navigoStore;
  const {
    isReschedule,
    currentServices,
    isForFamily,
    currentAppointment,
    loadingSlots,
    appointmentDate,
    timeSlot,
    slots,
    dateArr,
    dateSelected,
    sltInd,
    callReschedule,
    familyMemberSelected,
    familyMemberSelectedData,
    addressSelected,
    vendorId,
    vendorCode,
    isUserSelectedFamily,
    isSmoBooking,
    smoDraft,
  } = navigoStore.ui;

  // Reason screen is only used for consultation (serviceType === 1), and even
  // there CMP skips it. Second Medical Opinion now collects its reason/files
  // up-front (before this screen), so it also skips the reason screen here and
  // books directly, merging the collected draft below.
  const skipReasonScreen = isCmp || serviceType !== 1 || isSmoBooking;

  useEffect(() => {
    dispatch(
      setStateFromPayload({
        timeSlot: null,
        sltInd: null,
        appointmentDate: null,
      }),
    );

    dispatch(
      logEventWithData({
        action: "navigo_open_schedule_slot",
        data: {
          is_app_event: true,
          serviceID: currentServices.ID,
          serviceName: currentServices.name,
        },
      }),
    );

    let dateArr = createDateAndChangeScreen(
      addressId || isCmp ? config.minBookingDays : provider.minBookingDays,
      addressId || isCmp ? config.maxAdvBookingDays : provider.maxBookingDays,
    );

    dispatch(
      setStateFromPayload({
        dateArr: dateArr,
      }),
    );

    getTimeSlots(dateArr[0].exactDate, 0);
  }, []);
  useEffect(() => {
    if (!loadingSlots && slots.length === 0) {
      dispatch(
        setStateFromPayload({
          timeSlot: null,
          sltInd: null,
        }),
      );
    }
  }, [slots, loadingSlots]);
  useEffect(() => {
    if (callReschedule) {
      let body = {
        appointmentID: currentAppointment.appointment.ID,
        appointmentDate: new Date(
          moment(appointmentDate).add(1, "h").format(),
        ).toISOString(),
        timeSlot: stringToIntTime(timeSlot.time),
      };
      dispatch(rescheduleAppointments(body));
    }
  }, [callReschedule]);

  const handleRescheduleAndBooking = async () => {
    const source = localStorage.getItem("app_source") === "pb" ? "pb" : "mli";
    if (isReschedule) {
      let body = {
        metadata: { source: source },
        appointmentID: currentAppointment.appointment.ID,
        addressId: currentAppointment.appointment.addressId,
        appointmentDate: new Date(
          moment(appointmentDate).add(1, "h").format(),
        ).toISOString(),
        timeSlot: stringToIntTime(timeSlot.time),
        timeSlotId: timeSlot.id,
        vendor: currentAppointment.appointment.vendorId,
        vendorCode: currentAppointment.appointment.vendorCode,
      };

      dispatch(rescheduleAppointments(body));
    } else {
      let body = {
        metadata: { source: source },
        addressId: addressId,
        patientID: patientProfile?.id,
        planID: services?.planId,
        bookedFor: familyMemberSelected === 0 ? 0 : familyMember,
        serviceID: services?.serviceId,
        status: 0,
        mode: services?.serviceModeDetails[0].modeName,
        bookingDate: new Date().toISOString(),
        appointmentDate: new Date(
          moment(appointmentDate).add(1, "h").format(),
        ).toISOString(),
        timeSlot: stringToIntTime(timeSlot.time),
        timeSlotId: timeSlot?.id,
        vendorId: vendorId,
        providerAutoSelect: false,
        memberRelation: memberRelation,
        serviceProviderId: timeSlot?.serviceProviderId,
        vendorCode: vendorCode,
      };
      if (addressId == null || addressId == "" || addressId == undefined) {
        if (skipReasonScreen) {
          // For CMP / non-consultation, book directly with reason-screen
          // defaults. For Second Medical Opinion, merge the reason/contact the
          // user filled up-front on the (now collect-only) reason screen.
          const finalBody = {
            ...body,
            reasonId: isSmoBooking ? smoDraft?.reasonId ?? 7 : 1,
            reasonDetails: isSmoBooking ? smoDraft?.reasonDetails ?? "" : "",
            isCustomContactNumber: isSmoBooking
              ? !!smoDraft?.isCustomContactNumber
              : false,
            customContactNumber: isSmoBooking
              ? smoDraft?.customContactNumber ?? null
              : null,
          };

          // SMO documents to attach (prescriptions + lab reports). Uploaded
          // AFTER the appointment exists — right here for free, and post-payment
          // in BookAfterPayment for paid (see below).
          const smoFiles =
            isSmoBooking && smoDraft
              ? [
                  ...(smoDraft.prescriptionFiles || []).map((f) => ({
                    ...f,
                    category: "prescription",
                  })),
                  ...(smoDraft.labReportFiles || []),
                ]
              : [];

          if (price !== 0) {
            dispatch(logEvent({ action: `click payment` }));
            finalBody.orderId = "";
            // Paid: don't upload yet. Stash the files so BookAfterPayment can
            // upload + map them AFTER the v1 zpay status comes back successful.
            if (smoFiles.length > 0) {
              dispatch(
                setStateFromPayload({
                  pendingPaidDocs: {
                    files: smoFiles,
                    patientId: patientProfile.id,
                  },
                }),
              );
            }
            startPayment(finalBody);
            return;
          }

          // Free: create the appointment, then upload + map its documents.
          setIsLoading(true);
          dispatch(createAppointments(finalBody))
            .unwrap()
            .then(async (res) => {
              dispatch(logEvent({ action: `Confirm appointment` }));
              if (isSmoBooking) {
                const appointmentId = res?.data?.id;
                if (appointmentId && smoFiles.length > 0) {
                  const docIds = await uploadNavigoDocuments(
                    smoFiles,
                    patientProfile.id,
                  );
                  await mapDocumentsToAppointment(appointmentId, docIds);
                }
                dispatch(
                  setStateFromPayload({ isSmoBooking: false, smoDraft: null }),
                );
              }
              navContext.navigate(
                `/mliNavigo/bookingCard?date=${moment(appointmentDate).format(
                  "DD MMM, YYYY",
                )}&time=${timeSlot.time}`,
                "none",
                "pop",
              );
              setIsLoading(false);
            })
            .catch((err) => {
              console.error("Error booking appointment:", err);
              setIsLoading(false);
            });
        } else {
          dispatch(getReasons("true"));
          history.push({
            pathname: "/reasonScreen",
            state: withFrom(location, { body, services, price, pageType }),
          });
        }
      }
      if (addressId) {
        console.log("inside address if");
        console.log("inside price if", price);
        if (price != 0) {
          console.log("inside price if", price);
          dispatch(logEvent({ action: `click payment` }));
          body.orderId = "";
          startPayment(body);
          return;
        }
        setIsLoading(true);
        dispatch(createAppointments(body))
          .unwrap()
          .then(async (res) => {
            dispatch(logEvent({ action: `Confirm appointment` }));

            navContext.navigate(
              `/mliNavigo/bookingCard?date=${moment(appointmentDate).format(
                "DD MMM, YYYY",
              )}&time=${timeSlot.time}`,
              "none",
              "pop",
            );
            setIsLoading(false);
          })
          .catch((err) => {
            console.error("Error booking appointment:", err);
            setIsLoading(false);
          });
      }
    }
  };
  const startPayment = (body) => {
    try {
      navigoServices
        .generateOrderForNavigo(body)
        .then((result) => {
          const res = result.data;

          if (window.Capacitor.platform !== "web") {
            setTimeout(() => {
              let url = `${process.env.REACT_APP_ZYLA_PAY_URL}?orderId=${res.orderId}&redirect=https://ep3z4.app.goo.gl/oi43`;
              window.open(url, "_top");
            });
          }

          dispatch(
            setStateFromPayload({
              openZylaPay: true,
              paymentLoaderOrderId: res.orderId,
            }),
          );
        })
        .catch((err) => {
          let errMsg = "Couldn't create appointment.";

          if (err && err.message) {
            errMsg = errorMessageHandler(
              err?.response?.data,
              "Couldn't create appointment.",
            );
          }
          dispatch(navigoShowError({ msg: errMsg }));
        });
    } catch (err) {
      dispatch(navigoShowError({ msg: "Something went wrong." }));
    }
  };

  const getTimeSlots = (date, dateSelected = 0) => {
    try {
      let body = {
        bookingMode: services?.serviceModeDetails[0]?.modeName,
        collectionDate: moment(date).format("YYYY-MM-DD"),
        planId: services?.planId,
        serviceId: services?.serviceId,
        serviceProviderId: provider?.id,
        vendor: "",
        vendorCode: "",
        addressId: addressId,
      };

      if (isReschedule) {
        body = {
          ...body,
          vendor: currentAppointment.appointment.vendorId,
          vendorCode: currentAppointment.appointment.vendorCode,
        };
      }
      dispatch(
        getAllTimeSlots({
          data: body,
          dateSelected: dateSelected,
          appointmentDate: date,
        }),
      );
      // }
      // else {
      //   let body = {
      //     serviceId: currentServices.ID,
      //     date: moment(date).format("YYYY-MM-DD"),
      //   };
      //   dispatch(
      //     getNavigoServicesSlots({
      //       data: body,
      //       dateSelected: dateSelected,
      //       appointmentDate: date,
      //     })
      //   );
      // }
    } catch (err) {
      dispatch(navigoShowError({ msg: "Something went wrong." }));
    }
  };

  return (
    <>
        {" "}
        {isLoading && (
          <div className="booking-loader-blur">
            <div className="w-10 h-10 mb-4 border-4 border-t-4 border-[#97144D] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-[#97144D] font-medium text-lg">
              Creating your appointment...
            </p>
          </div>
        )}
        <div className="navigo-booking-screen-3-container">
          {/* <div className="navigo-booking-screen-3-header">
      <img
        src={backImage}
        alt="icon"
        onClick={() => {
          history.goBack();
          if (isReschedule) {
            dispatch(
              setStateFromPayload({
                currScreen: 1,
                isReschedule: false,
                currentAddressSelected: null,
              })
            );
          } else {
            if (isForFamily && !isUserSelectedFamily) {
              history.goBack();
              dispatch(updateCurrentScreen(1));
            } else {
              history.goBack();
              dispatch(updateCurrentScreen(2));
            }
          }
        }}
      />
    </div> */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  width: 32,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <BackButton onBack={onBack} />
              </div>
              <p
                className="navigo-booking-screen-3-mid-service-name"
                style={{ flex: 1, margin: 0 }}
              >
                {/* Prefer the service passed in over redux: the prop is the
                    service being booked, while currentServices is shared
                    mutable state that a flow can leave stale. */}
                {isReschedule
                  ? currentAppointment.service.name
                  : services?.name || currentServices.name}
              </p>
              <div style={{ width: 32, flexShrink: 0 }} />
            </div>
            <div className="navigo-booking-screen-3-mid">
              {isReschedule ? (
                <div className="navigo-booking-screen-3-mid-progress">
                  <div className="navigo-booking-container-section-3-ele">
                    <SingleAppointmentComponent
                      serviceName={"Old appointment"}
                      displayName={currentAppointment.appointment?.displayName}
                      date={currentAppointment.appointment?.appointmentDate}
                      timeSlot={currentAppointment.appointment?.timeSlot}
                    />
                  </div>
                </div>
              ) : (
                <div className="navigo-booking-screen-3-mid-progress">
                  <ProgressIndicator isInput={true} />
                </div>
              )}
              <div className="navigo-booking-screen-3-calendar">
                {dateArr &&
                  dateArr.length > 0 &&
                  dateArr.map((dt, dateIndex) => {
                    return (
                      <div
                        className={`navigo-booking-screen-3-calendar-date ${
                          dateSelected === dateIndex
                            ? "navigo-booking-screen-3-calendar-date-selected "
                            : ""
                        }`}
                        onClick={() => {
                          getTimeSlots(dt.exactDate, dateIndex);
                        }}
                      >
                        <span>
                          {dt.day}&nbsp;{dt.month}
                        </span>
                        &nbsp;
                      </div>
                    );
                  })}
              </div>
              <div className="navigo-booking-screen-3-bottom-parent">
                <div className="navigo-booking-screen-3-bottom">
                  {!loadingSlots &&
                    slots &&
                    slots.length > 0 &&
                    slots.map((slt) => {
                      return (
                        <div
                          className={`navigo-booking-screen-3-bottom-ele ${
                            timeSlot && timeSlot.id === sltInd
                              ? "navigo-booking-screen-3-bottom-ele-selected"
                              : ""
                          }`}
                          onClick={() => {
                            dispatch(
                              setStateFromPayload({
                                timeSlot: slt,
                                sltInd: slt.id,
                              }),
                            );
                          }}
                          key={slt.id}
                        >
                          <p>{slt.time}</p>
                          {timeSlot && slt.id === timeSlot.id && (
                            <img src={tickimg} alt="icon" />
                          )}
                        </div>
                      );
                    })}
                  {!loadingSlots && slots.length === 0 && (
                    <div className="navigo-booking-screen-3-bottom-no-slots">
                      <img src={noSlotVector} alt="bg-img" />
                      <p>
                        Oops! no free slot available,
                        <br /> try some other date
                      </p>
                    </div>
                  )}
                  {loadingSlots && (
                    <CircularProgress className="navigo-no-slots-loader"></CircularProgress>
                  )}
                </div>
              </div>
              <div className="navigo-booking-screen-3-button">
                <button
                  disabled={
                    !timeSlot ||
                    !appointmentDate ||
                    loadingSlots ||
                    slots.length === 0
                  }
                  onClick={handleRescheduleAndBooking}
                >
                  {isReschedule ? "Reschedule" : "Continue"}
                </button>
              </div>
            </div>
          </div>
        </div>
    </>
  );
}

export default function NavigoFinalSection(props) {
  return (
    <IonPage>
      <IonContent>
        <NavigoFinalSectionContent {...props} />
      </IonContent>
    </IonPage>
  );
}
